import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTESWEB } from "@repo/utils";
import type { Video } from "../../../../types/reviseTypes";
import styles from "./ReviseByVideos.module.scss";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import { Icon } from "@repo/ui";
import { SectionHeader } from "../../../../components/SectionHeader";
import { formatDurationCompact } from "../../../../lib/formatUtils";
import { REVISE_VIDEOS_PLAYLIST_TYPE_ID } from "../revisePlaylistConstants";
import type { VideoPlayerBreadcrumbItem } from "../../../selflearn/SelfLearnVideoPlayerPage";

type ReviseByVideoSectionProps = {
  videos: Video[];
  subjectId: number;
  subjectName: string;
  chapterId: number;
  chapterName: string;
  widgetTitle: string;
  videoTypeIds: number[];
};

const SELF_LEARN_BASE = `/${ROUTESWEB.APP.SELF_LEARN}`;
const CHAPTER_LIST_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}`;
const CHAPTER_DETAILS_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}`;

export const ReviseByVideoSection = ({
  videos,
  subjectId,
  subjectName,
  chapterId,
  chapterName,
  widgetTitle,
  videoTypeIds,
}: ReviseByVideoSectionProps) => {
  const navigate = useNavigate();
  const selfLearnBasePath = useMemo(
    () => `/${ROUTESWEB.APP.SELF_LEARN}`.replace(/\/$/, ""),
    [],
  );

  const videoPlayerPath = useMemo(
    () =>
      `${selfLearnBasePath}/${SELF_LEARN_ROUTES.VIDEO_PLAYER}`.replace(
        /\/$/,
        "",
      ),
    [selfLearnBasePath],
  );

  const openVideoPlayer = useCallback(
    (index: number) => {
      if (index < 0 || index >= videos.length) return;
      const list = videos.map((v) => ({
        ...v,
        subjectId,
      }));
      // Unique search avoids React Router treating a repeat push to the same
      // pathname as a no-op after navigating back from the player.
      navigate(
        `${videoPlayerPath}?_=${encodeURIComponent(String(Date.now()))}`,
        {
          state: {
            title: widgetTitle,
            item: list[index],
            videos: list,
            index,
            playlistId: REVISE_VIDEOS_PLAYLIST_TYPE_ID,
            breadcrumbItems: [
              {
                label: "Learn",
                mode: "replace",
                delta: -1,
                to: SELF_LEARN_BASE,
              },
              {
                label: subjectName,
                mode: "replace",
                delta: -1,
                to: CHAPTER_LIST_HREF,
                state: {
                  subjectId,
                  subjectName,
                },
              },
              {
                label: chapterName,
                mode: "replace",
                delta: -1,
                to: CHAPTER_DETAILS_HREF,
                state: {
                  chapterId,
                  chapterName,
                  subjectId,
                  subjectName,
                  initialTab: "revise",
                },
              },
              {
                label: list[index].title,
                isCurrent: true,
              },
            ] as VideoPlayerBreadcrumbItem[],
          },
        },
      );
    },
    [navigate, videos, subjectId, videoPlayerPath, widgetTitle],
  );

  return (videos?.length ?? 0) > 0 ? (
    <section className={styles.section}>
      <SectionHeader
        title={widgetTitle}
        showViewAll={videos.length > 5}
        onViewAll={() =>
          navigate(`../${SELF_LEARN_ROUTES.REVISE_VIDEOS_VIEW_ALL}`, {
            state: {
              subjectId,
              subjectName,
              chapterId,
              chapterName,
              widgetTitle,
              videoTypeIds,
            },
          })
        }
      />
      <div className={styles.videoList} role="list">
        {videos.slice(0, 5).map((item, index) => (
          <div
            key={`${item.videoId}-${index}`}
            className={styles.videoListCardWrap}
            role="listitem"
          >
            <VideoCard
              title={item.title}
              thumbnail={item.thumbnailURL}
              duration={formatDurationCompact(item.duration)}
              views={item.views}
              progressFraction={Math.min(
                1,
                Math.max(0, (item.progress ?? 0) / 100),
              )}
              onOpen={() => openVideoPlayer(index)}
            />
          </div>
        ))}
      </div>
    </section>
  ) : null;
};

function VideoCard({
  title,
  thumbnail,
  duration,
  views,
  progressFraction,
  onOpen,
}: {
  title: string;
  thumbnail: string;
  views: number;
  duration: string;
  progressFraction: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation("selflearn");
  return (
    <div className={styles.videoCard} onClick={onOpen}>
      <div className={styles.videoMedia}>
        <img className={styles.videoThumbnail} src={thumbnail} alt={title} />

        <div className={styles.viewsPill}>
          <Icon name="visibility" size={16} color="var(--colors-neutral-50)" />
          <span className={styles.pillText}>
            {views} {t("revise.reviseByVideos.views")}
          </span>
        </div>

        <button
          type="button"
          className={styles.playButton}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          aria-label={t("revise.reviseByVideos.playVideoAria")}
        >
          <Icon
            name="playIcon"
            width={11}
            height={14}
            color="var(--colors-primary-40)"
          />
        </button>

        <div className={styles.durationPill}>
          <span className={styles.pillText}>{duration}</span>
        </div>

        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressFraction * 100}%` }}
          />
        </div>
      </div>

      <p className={styles.videoTitle}>{title}</p>
    </div>
  );
}
